import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getOtherStudentInfo,
  getProfileModified,
  getStudentCreditHistory,
  getStudentDashboard,
  updateStudentProfile,
  uploadProfilePics,
  verifyMemberByPhoneNumber,
} from "../query/dashboard";
import toast from "react-hot-toast";
import { OtherStudentInfo, UpdateProfileData } from "../../../types/dashboard";
import { useRouter } from "next/navigation";
import { isAuthenticated } from "../utils/session";

const studentDashboardOptions = {
  queryKey: ["studentDashboard"],
  queryFn: getStudentDashboard,
  retry: false,
  // This endpoint also records a login (IP + browser), so don't refetch it casually.
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
};

export const useStudentDashboard = () => {
  return useQuery(studentDashboardOptions);
};

/**
 * For shared UI (Header, cards) that also renders on public pages.
 * Only fetches when a token exists, so logged-out visitors never trigger a 401.
 * Shares the same cache entry as useStudentDashboard, so it's one request total.
 */
export const useCurrentStudent = () => {
  const { data } = useQuery({
    ...studentDashboardOptions,
    enabled: typeof window !== "undefined" && isAuthenticated(),
  });
  return data?.student ?? null;
};

export const useProfileModified = () => {
  return useQuery({
    queryKey: ["isProfileModified"],
    queryFn: getProfileModified,
  });
};

export const useStudentCreditHistory = (
  studentId?: number | string,
  startDate?: string,
  endDate?: string,
) => {
  return useQuery({
    queryKey: ["studentCreditHistory", studentId, startDate, endDate],
    queryFn: () =>
      getStudentCreditHistory(
        studentId as number,
        startDate as string,
        endDate as string,
      ),
    enabled: Boolean(studentId) && Boolean(startDate) && Boolean(endDate),
  });
};

export const useUpdateProfile = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProfileData) => {
      await updateStudentProfile(data);
      // Fetch updated data after successful update
      const updatedData = await getStudentDashboard();
      return updatedData;
    },
    onSuccess: (data) => {
      // Put the fresh data straight into the cache (no second fetch needed)
      queryClient.setQueryData(["studentDashboard"], data);
      toast.success("Profile updated successfully!");
      router.push("/dashboard");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update profile");
    },
  });
};

export const useVerifyPhoneNumber = () => {
  return useMutation({
    mutationFn: async (phoneNumber: string) => {
      const data = await verifyMemberByPhoneNumber(phoneNumber);
      if (data && data.length > 0) {
        const member = data[0];

        const name = `${member.firstName} ${member.middleName || ""} ${
          member.lastName
        }`.trim();

        // Map role to position
        const getPosition = (role: number) => {
          switch (role) {
            case 1:
              return "Facilitator";
            case 2:
              return "Training Provider";
            case 0:
              return "Facilitator Support Associate";
            case -1:
              return "Digital Volunteer";
            default:
              return "Unknown";
          }
        };

        // Map isActive to status
        const getStatus = (isActive: number) => {
          switch (isActive) {
            case 1:
              return "Active";
            case 0:
              return "Inactive";
            case 2:
              return "Not found, Contact La Plage Meta Verse for more information";
            case 3:
              return "Dismissal, Do business with him/her at your own risk";
            default:
              return "Unknown";
          }
        };

        // Map isActive to verified
        const getVerified = (isActive: number) => {
          return isActive === 1 ? "Yes" : "No";
        };

        return {
          name,
          position: getPosition(member.role),
          status: getStatus(member.isActive),
          verified: getVerified(member.isActive),
        };
      } else {
        return null;
      }
    },

    onError: (error: any) => {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "An error occurred during verification. Please try again.",
      );
    },
  });
};

export const useUploadProfilePics = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      studentId,
      formData,
    }: {
      studentId: number;
      formData: FormData;
    }) => {
      return await uploadProfilePics(studentId, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studentDashboard"] });
      toast.success("Profile picture updated successfully!");
    },
    onError: (error: any) => {
      console.error("Upload error:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to upload image. Please try again.",
      );
    },
  });
};

export const useOtherStudentInfo = () => {
  return useMutation({
    mutationFn: async (email: string): Promise<OtherStudentInfo> => {
      return await getOtherStudentInfo(email);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message ||
          "No student found with that email. Please check and try again.",
      );
    },
  });
};
